/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class YoungAudiencesArtsForLearning extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/ya-young-audiences-arts-for-learning", market);
    const alt = "Lasting Smiles";
    const title = "Young Audiences Arts for Learning · Partners · Lasting Smiles";
    const description = "Give back to Young Audiences Arts for Learning with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical do good lip care and lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/ya-young-audiences-arts-for-learning", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/ya-young-audiences-arts-for-learning", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/ya-young-audiences-arts-for-learning", market),
                    "name": "Young Audiences Arts for Learning",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/ya-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/ya-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Young Audiences Arts for Learning"
                className="block mx-auto"
                src={require("../../public/images/logos/ya-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Young&nbsp;Audiences Arts&nbsp;for&nbsp;Learning
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              Our mission is to inspire young people and expand their learning through the arts.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Young Audiences Arts for Learning Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/ya/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Young Audiences Arts for Learning Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/ya/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Young Audiences Arts for Learning Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/ya/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/ya/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Young Audiences Arts for Learning Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/ya/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              From dance to media arts and storytelling to sound engineering, we believe the arts are vital to the education and social emotional development of all students.
            </h2>
            <p className="text-center">
              We are the nation's largest arts in education network, comprised of locally affiliated nonprofits that impact over 5 million young people each year.
              When schools were forced to close due to the COVID-19 pandemic, our network pivoted quickly by building online arts education programming and by sending art supplies to students at home for continued hands-on learning experiences.
              The arts have a healing power that cannot be replaced, especially during these challenging times.
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Watch how Young Audiences creates lasting&nbsp;smiles for&nbsp;children.
            </h2>
            <LazyLoad
              className="min-h-200 relative sm:min-h-400 my-5"
              offset={250}
            >
              <iframe
                allowFullScreen={true}
                className="absolute h-full left-0 top-0 w-full"
                src="https://player.vimeo.com/video/112744743"
                title="A Spotlight on Young Audiences Arts for Learning"
              />
            </LazyLoad>
          </div>
          <div className="text-center">
            <Link
              as={toRelativePageURL("/products", market as string)}
              href={{
                pathname: "/products",
                query: { market },
              }}
            >
              <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                Shop &amp; Make a Difference
              </a>
            </Link>
          </div>
        </section>
      </>
    );
  }
}
